// ────────────────────────────────────────────────────────────────────────────
// App-Group frame poller.
//
// The iOS Broadcast Upload Extension (SampleHandler.swift) writes every
// captured frame as `frames/<unixMs>.jpg` into the shared App Group
// container, AND a legacy `latest_frame.jpg` for the Darwin-notification
// preview path that BroadcastAutoStart exposes via the JS `frame` event.
//
// Until now the live-scan pipeline only consumed the legacy preview frame:
// LiveScanModal listened for `frame` JS events and never touched the
// per-frame JPEGs on disk. Two problems with that:
//
//   1. Darwin notifications coalesce when the host app is backgrounded
//      (typical during a ReplayKit broadcast — the trainer is in Pokémon
//      Go), so most of the per-frame `com.nexusdex.broadcast.frame` posts
//      were dropped. The extension wrote N frames; the JS layer only ever
//      saw one or two `frame` events.
//
//   2. Backlog accumulates. Status snapshots from real-device runs showed
//      framesOnDisk in the high hundreds — left over from prior broadcasts
//      that never had a host-side drain. Even when the JS bridge did fire,
//      the legacy frame the host read was the most recently written file
//      and the disk-backed timestamped frames sat unread until the user
//      manually tapped "Clear frames" in the Settings diagnostics panel.
//
// This module fixes both by polling the App Group container directly while
// the live scan is active: every TICK_MS we listFrames(), consume any new
// JPEGs, and hand them to the same `dispatchFrame` the JS-bridge path
// uses. Frames whose unixMs timestamp is older than the session start are
// dropped as stale (with a counter), so a 1024-frame backlog never bleeds
// into the current run.
//
// The poller is platform-aware: on web / Android / iOS without the plugin
// it short-circuits to a no-op so callers can wire it unconditionally.
// ────────────────────────────────────────────────────────────────────────────
import {
  broadcastBridge,
  type BroadcastFrameMeta,
} from "./broadcast-bridge";
import type { BroadcastFrame } from "./broadcastAutoStart";

export interface AppGroupFramePollerOptions {
  /**
   * Unix-millisecond timestamp marking the start of the current live-scan
   * session. Frames whose filename timestamp is older than this are
   * skipped as stale and counted under `framesSkippedStale`. Defaults to
   * Date.now() at construction time.
   */
  sessionStartMs?: number;
  /**
   * Poll interval, in milliseconds. Defaults to 1000 — the SampleHandler
   * already throttles JPEG writes to 1Hz so polling faster would just
   * burn battery on idle listFrames calls.
   */
  tickMs?: number;
  /**
   * Maximum number of frames to consume per tick. Defaults to 4. A higher
   * bound on a single tick lets us catch up after a foreground transition
   * without falling further behind, but each consumed frame holds a JPEG
   * in memory until dispatchFrame() resolves so the cap also pre-empts
   * runaway memory growth.
   */
  maxPerTick?: number;
  /**
   * Sink for every consumed frame. The caller is responsible for routing
   * the frame into the live-scan OCR dispatch path. We pass the
   * BroadcastFrame shape so the JS-bridge path and the disk-backed path
   * funnel through one downstream pipeline.
   */
  onFrame: (frame: BroadcastFrame) => void;
  /**
   * Optional structured logger. Mirrors LiveScanModal's `liveDebugLog`
   * shape so a single grep over the console traces both paths. Stays
   * silent in production unless SCAN_DEBUG_MODE is enabled by the caller.
   */
  log?: (step: string, payload?: Record<string, unknown>) => void;
  /**
   * Dependency-injection hook for the broadcastBridge. Defaults to the
   * production wrapper; the unit tests override it with a fake so the
   * stale-frame filter and consume-success/failure paths can be driven
   * deterministically without an iOS device.
   */
  bridge?: AppGroupBridge;
  /**
   * Optional back-pressure probe. When this returns true the poller
   * skips consuming any frames for this tick (the JPEGs stay on disk
   * for the SampleHandler's own retention sweep). Used to pause the
   * disk drain while the OCR pool is saturated so we don't pull base64
   * payloads into JS memory only to drop them at dispatch — that was
   * the load-bearing amplifier of the 1.2.1(5) WKWebView memory blowup.
   */
  isBusy?: () => boolean;
}

/**
 * The slice of `broadcastBridge` the poller depends on. Defined as a
 * standalone interface so tests can supply a fake without pulling in
 * @capacitor/core.
 */
export interface AppGroupBridge {
  listFrames(): Promise<BroadcastFrameMeta[]>;
  consumeFrame(path: string): Promise<{ base64: string; sizeBytes: number } | null>;
}

export interface AppGroupFramePollerStats {
  /** Number of listFrames() ticks that have run. */
  ticks: number;
  /** Frames consumed from the App Group container (post-stale-filter). */
  framesConsumed: number;
  /** Frames dispatched into the OCR pipeline via onFrame. */
  framesDispatched: number;
  /**
   * Frames whose filename timestamp predates `sessionStartMs`. These are
   * deleted (via consumeFrame, which deletes the file) so they don't
   * mask the current run's frames on subsequent ticks, but never reach
   * `onFrame`.
   */
  framesSkippedStale: number;
  /**
   * consumeFrame returned null — typically the file was deleted between
   * the listFrames sort and the consume call.
   */
  framesConsumeFailed: number;
  /** consumeFrame returned bytes but base64 decode produced no payload. */
  framesDecodeFailed: number;
  /**
   * Frames the poller saw on disk in a tick but didn't read because we
   * already hit `maxPerTick`. They remain on disk for the next tick.
   */
  framesDeferred: number;
  /** Most recent listFrames() result size. */
  lastSeenOnDisk: number;
  /** Most recent listFrames() error, if any. */
  lastError: string | null;
  /** Ticks where `isBusy()` returned true and we skipped consuming. */
  ticksSkippedBusy: number;
}

const DEFAULT_TICK_MS = 1000;
const DEFAULT_MAX_PER_TICK = 4;
// When OCR is saturated we tolerate up to this many fresh JPEGs sitting
// on disk before we start pruning. Above the high-water mark the busy
// branch drains the OLDEST frames down to BUSY_BACKLOG_TARGET so the disk
// queue stays bounded and the next "not-busy" tick reads recent frames
// instead of the stale head of the queue. Numbers chosen so we keep ~1s
// of look-back (frames are written at 1Hz) while preventing the
// 1.2.1(6) framesOnDisk=68 scenario where every frame post-launch was
// older than the user's current screen.
const BUSY_BACKLOG_HIGH_WATER = 12;
const BUSY_BACKLOG_TARGET = 4;
// v1.2.1(15): cap the number of consumeFrame deletions per tick during
// the busy-prune branch. Without this cap an iOS background-resume that
// accumulated 100+ frames pinned the JS thread under ~100 sequential
// native bridge round-trips on the very first tick after resume — which
// is exactly the "freeze when switching back into Nexus Dex" report.
const BUSY_BACKLOG_PRUNE_MAX = 8;
// Cap on per-tick consumes during the final post-broadcast drain. The
// disk queue at this point holds whatever the SampleHandler wrote in
// the last second before broadcastFinished — typically <30 frames — so
// a single drain pass at 16-deep can clear it in one go. We still bound
// it so a runaway 1000-frame backlog cannot blow up JS memory.
const DRAIN_MAX_PER_TICK = 16;

/**
 * Create the poller. Caller invokes `.start()` when the session begins and
 * `.stop()` on finish / unmount. `.drain()` runs one final pass (useful
 * after broadcastFinished to flush any frames the extension wrote after
 * we stopped polling).
 *
 * Stats are exposed by ref so the LiveScanModal can include them in the
 * end-of-session telemetry note without paying a render-on-every-tick.
 */
export function createAppGroupFramePoller(opts: AppGroupFramePollerOptions) {
  const sessionStartMs = opts.sessionStartMs ?? Date.now();
  const tickMs = opts.tickMs ?? DEFAULT_TICK_MS;
  const maxPerTick = opts.maxPerTick ?? DEFAULT_MAX_PER_TICK;
  const log = opts.log ?? (() => {});
  const bridge: AppGroupBridge = opts.bridge ?? broadcastBridge;

  const stats: AppGroupFramePollerStats = {
    ticks: 0,
    framesConsumed: 0,
    framesDispatched: 0,
    framesSkippedStale: 0,
    framesConsumeFailed: 0,
    framesDecodeFailed: 0,
    framesDeferred: 0,
    lastSeenOnDisk: 0,
    lastError: null,
    ticksSkippedBusy: 0,
  };
  const isBusy = opts.isBusy;

  let timer: ReturnType<typeof setInterval> | null = null;
  let tickRunning = false;
  let stopped = false;

  async function runTick(opts2?: { ignoreBusy?: boolean; capOverride?: number }): Promise<void> {
    if (tickRunning || stopped) return;
    tickRunning = true;
    stats.ticks += 1;
    const ignoreBusy = !!opts2?.ignoreBusy;
    const cap = opts2?.capOverride ?? maxPerTick;
    try {
      // Back-pressure: if the consumer (OCR pool) is saturated, skip the
      // *consume-and-dispatch* phase but still listFrames so we can prune
      // the oldest backlog. Without this prune, a slow Tesseract pass on
      // iOS leaves dozens of JPEGs piling on disk (1.2.1(6) report:
      // framesOnDisk=68, framesConsumed=0). When the OCR pool finally
      // frees up we want the *newest* frames — the trainer has scrolled
      // past whatever was on screen when the backlog began. Avoids
      // pulling base64 into JS only to drop it at dispatch (the 1.2.1(5)
      // WKWebView memory blowup amplifier).
      const busy = !ignoreBusy && isBusy ? isBusy() : false;
      if (busy) {
        stats.ticksSkippedBusy += 1;
        log("app_group_tick_skipped_busy");
        // While saturated we still take a peek at the backlog so we can
        // delete the oldest fresh frames before they grow unbounded.
        // Without this, framesOnDisk climbs into the hundreds while OCR
        // chews on a single Tesseract pass — exactly the 1.2.1(6) symptom.
        try {
          const peek = await bridge.listFrames();
          stats.lastSeenOnDisk = peek.length;
          const freshPeek = peek.filter((f) => f.timestamp >= sessionStartMs);
          if (freshPeek.length > BUSY_BACKLOG_HIGH_WATER) {
            const sortedAsc = [...freshPeek].sort((a, b) => a.timestamp - b.timestamp);
            const rawDrop = freshPeek.length - BUSY_BACKLOG_TARGET;
            // v1.2.1(15): cap the per-tick prune so a 100-frame backlog
            // after the user backgrounded the app cannot freeze the
            // bridge for 100 awaits on the next tick. Sustained backlog
            // is drained over multiple ticks. Build 14 freeze report:
            // "freezing when we switch window back into Nexus Dex".
            const dropCount = Math.min(rawDrop, BUSY_BACKLOG_PRUNE_MAX);
            for (let i = 0; i < dropCount && i < sortedAsc.length; i += 1) {
              await bridge.consumeFrame(sortedAsc[i].path).catch(() => null);
              stats.framesSkippedStale += 1;
              stats.framesDeferred = Math.max(0, stats.framesDeferred - 1);
            }
            log("app_group_busy_backlog_pruned", {
              before: freshPeek.length,
              dropped: dropCount,
              target: BUSY_BACKLOG_TARGET,
              capped: rawDrop > BUSY_BACKLOG_PRUNE_MAX,
            });
          }
        } catch (err) {
          // Peek is best-effort. Failing here must not derail the
          // happy-path tick — the next tick will retry.
          log("app_group_busy_peek_failed", { error: String(err) });
        }
        return;
      }
      const frames = await bridge.listFrames();
      stats.lastSeenOnDisk = frames.length;
      if (frames.length === 0) return;

      // Partition stale (pre-session) from current. The stale ones are
      // consumed too (their consume call deletes them) so they don't keep
      // masquerading as fresh frames on the next tick.
      const stale = frames.filter((f) => f.timestamp < sessionStartMs);
      const fresh = frames.filter((f) => f.timestamp >= sessionStartMs);

      if (stale.length > 0) {
        log("app_group_stale_frames", {
          count: stale.length,
          sessionStartMs,
        });
        // Consume + drop. We don't dispatch these. v1.2.1(15) caps the
        // per-tick purge at BUSY_BACKLOG_PRUNE_MAX (was maxPerTick * 8
        // = 32) so a 1000-frame backlog after iOS resume cannot freeze
        // the bridge on a single tick. Sustained backlogs drain over
        // multiple ticks; this trades drain throughput for UI
        // responsiveness on app-foreground transitions.
        const drainCap = Math.min(stale.length, BUSY_BACKLOG_PRUNE_MAX);
        for (let i = 0; i < drainCap; i += 1) {
          const f = stale[i];
          await bridge.consumeFrame(f.path).catch(() => null);
          stats.framesSkippedStale += 1;
        }
      }

      // v1.2.1(7): prefer the NEWEST frames. The trainer has scrolled
      // past whatever was on screen when the older frames were written,
      // and OCR'ing newer frames is the best chance of catching the row
      // they're actively looking at. listFrames returns timestamp-sorted
      // (asc) in current bridges; sort defensively so the slice is
      // deterministic. Older frames stay on disk until the next tick or
      // the busy-prune above sweeps them.
      const freshDesc = [...fresh].sort((a, b) => b.timestamp - a.timestamp);
      const toRead = freshDesc.slice(0, cap);
      if (freshDesc.length > cap) {
        stats.framesDeferred += freshDesc.length - cap;
      }

      for (const meta of toRead) {
        if (stopped) break;
        const consumed = await bridge.consumeFrame(meta.path).catch(() => null);
        if (!consumed) {
          stats.framesConsumeFailed += 1;
          log("app_group_consume_failed", { path: meta.path });
          continue;
        }
        if (!consumed.base64 || consumed.base64.length === 0) {
          stats.framesDecodeFailed += 1;
          log("app_group_decode_failed", { path: meta.path });
          continue;
        }
        stats.framesConsumed += 1;
        const dataUrl = `data:image/jpeg;base64,${consumed.base64}`;
        try {
          opts.onFrame({
            dataUrl,
            bytes: consumed.sizeBytes,
            ts: meta.timestamp,
          });
          stats.framesDispatched += 1;
        } catch (err) {
          log("app_group_dispatch_threw", { error: String(err) });
        }
      }
    } catch (err) {
      stats.lastError = err instanceof Error ? err.message : String(err);
      log("app_group_tick_failed", { error: stats.lastError });
    } finally {
      tickRunning = false;
    }
  }

  return {
    /** Start periodic polling. First tick fires synchronously (async). */
    start() {
      if (timer) return;
      stopped = false;
      void runTick();
      timer = setInterval(() => {
        void runTick();
      }, tickMs);
    },
    /** Stop polling. In-flight tick is allowed to finish naturally. */
    stop() {
      stopped = true;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    },
    /**
     * Force a final drain pass — used after broadcastFinished to catch
     * any frames the extension wrote in its last few hundred ms.
     *
     * v1.2.1(7): the drain pass ignores the `isBusy` back-pressure gate
     * and bumps the per-tick cap (DRAIN_MAX_PER_TICK). Once the broadcast
     * has finished no new frames will arrive, the OCR pool will start
     * freeing up within seconds, and the JS-side cost of bringing N
     * JPEGs into memory is bounded by the disk queue's actual size
     * (capped by the SampleHandler's own retention sweep). Without this
     * change, the 1.2.1(6) report's framesOnDisk=68 backlog was left on
     * disk because the legacy drain still respected `isBusy` and only
     * consumed up to 4 frames per pass.
     */
    async drain(): Promise<void> {
      // Avoid colliding with an in-flight tick.
      while (tickRunning) {
        await new Promise((r) => setTimeout(r, 25));
      }
      // Respect the caller's maxPerTick (some tests cap it at 2 to verify
      // deferred bookkeeping); only widen the per-tick cap when the
      // caller is using the default. The ignoreBusy override is always
      // safe — at drain time no new frames will be written, so allowing
      // a saturated OCR pool to receive late frames matches the late
      // post-finalize sweep semantics in LiveScanModal.
      const widened = maxPerTick === DEFAULT_MAX_PER_TICK
        ? DRAIN_MAX_PER_TICK
        : maxPerTick;
      await runTick({ ignoreBusy: true, capOverride: widened });
    },
    /**
     * Snapshot of current stats. Returned by reference so the caller can
     * read it at session-complete time without buffering deltas.
     */
    stats,
    /** Exposed so the diagnostics layer can confirm the boundary. */
    sessionStartMs,
  };
}

export type AppGroupFramePoller = ReturnType<typeof createAppGroupFramePoller>;
